// The desk's coin list, Binance-style: rows built from today's coins, the
// desk scan and the 24h tickers; search, stage filters and sorting.
import type { Ticker } from "@/hooks/useTickers";
import { archetypeLabel, type DeskRow, type Stage } from "./deskData";

export interface CoinRow {
  /** "SOLUSDT" */
  symbol: string;
  /** "SOL" */
  base: string;
  price: number | null;
  changePct: number | null;
  /** 24h volume, USDT. */
  volume: number | null;
  /** The desk's answer for the coin; null when it wasn't scanned. */
  stage: Stage | null;
  setup: { label: string; dir: "LONG" | "SHORT" } | null;
  favorite: boolean;
  /** In today's scanned list. */
  today: boolean;
}

export type SortKey = "stage" | "vol" | "price" | "chg";
export type SortDir = "asc" | "desc";
/** The stage chips: everything, or one step of the funnel. */
export type StageFilter = "all" | "ready" | "armed" | "stalking";

/** Most actionable first: Ready, Almost, a live Telegram signal, Waiting… */
export const STAGE_RANK: Record<Stage, number> = { ready: 0, armed: 1, signal: 2, stalking: 3, blocked: 4, "no-bias": 5 };

export function buildRow(
  symbol: string,
  opts: {
    row?: DeskRow | null;
    ready?: boolean;
    ticker?: Ticker | null;
    volumeM?: number | null;
    favorite: boolean;
    today: boolean;
  },
): CoinRow {
  const { row, ticker } = opts;
  const stage: Stage | null = opts.ready ? "ready" : row?.verdict.stage ?? null;
  return {
    symbol,
    base: symbol.replace(/USDT$/, ""),
    price: ticker?.price ?? row?.price ?? null,
    changePct: ticker?.changePct ?? null,
    // Today's coins keep the volume they were picked by (the scanner's); others the ticker's.
    volume: opts.volumeM != null ? opts.volumeM * 1e6 : ticker?.volume ?? null,
    stage,
    setup: row ? { label: archetypeLabel(row.checklist.archetype).toLowerCase(), dir: row.checklist.direction } : null,
    favorite: opts.favorite,
    today: opts.today,
  };
}

/** Search on the coin's name: "sol", "SOL", "solusdt" all find SOL. */
export function matches(row: Pick<CoinRow, "base">, query: string): boolean {
  const q = query.trim().toUpperCase().replace(/\/?USDT$/, "");
  return q === "" || row.base.includes(q);
}

export function passesStage(row: Pick<CoinRow, "stage">, filter: StageFilter): boolean {
  return filter === "all" || row.stage === filter;
}

const nullLast = (v: number | null, dir: SortDir) => (v === null ? (dir === "asc" ? Infinity : -Infinity) : v);

/** A sorted copy. "stage": most actionable first, then by volume. */
export function sortRows(rows: CoinRow[], key: SortKey, dir: SortDir): CoinRow[] {
  const sign = dir === "asc" ? 1 : -1;
  const by = (r: CoinRow): number => {
    if (key === "price") return nullLast(r.price, dir);
    if (key === "chg") return nullLast(r.changePct, dir);
    return nullLast(r.volume, dir);
  };
  return [...rows].sort((a, b) => {
    if (key === "stage") {
      const sa = a.stage === null ? 9 : STAGE_RANK[a.stage];
      const sb = b.stage === null ? 9 : STAGE_RANK[b.stage];
      if (sa !== sb) return sa - sb;
      return (b.volume ?? -1) - (a.volume ?? -1);
    }
    return sign * (by(a) - by(b));
  });
}

/** "$2.10B" / "$412M" / "$85.3K" */
export function compactUsd(v: number | null): string {
  if (v === null) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(v >= 1e8 ? 0 : 1)}M`;
  return `$${(v / 1e3).toFixed(1)}K`;
}

/** "+2.31%" / "−0.84%" (true minus). */
export function changeText(pct: number | null): string {
  if (pct === null) return "—";
  return `${pct >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(2)}%`;
}

// ─── The coin in the address bar: "/?token=SOL" ─────────────────────────────

export const DEFAULT_SYMBOL = "BTCUSDT";

/** "SOL", "sol", "SOLUSDT", "SOL/USDT" → "SOLUSDT"; null for anything that isn't a coin name. */
export function symbolFromToken(token: string | null): string | null {
  const base = (token ?? "").trim().toUpperCase().replace(/\/?USDT$/, "");
  return /^[A-Z0-9]{1,20}$/.test(base) ? `${base}USDT` : null;
}

/** "SOLUSDT" → "SOL", as the address bar shows it. */
export function tokenFromSymbol(symbol: string): string {
  return symbol.replace(/USDT$/, "");
}
